import {LitElement, css, html} from "lit";
import {mix} from "mixwith";
import StateMixin from "./Mixins/StateMixin.js";
import MutableValueMixin from "./Mixins/MutableValueMixin.js";
import "./aui-checkmark.js";

export class AuiToggle extends mix(LitElement).with(StateMixin, MutableValueMixin) {

	static get properties() {
		return {
			/**
			 * small|normal|medium|large
			 */
			size: { type: String, reflect: true },

			checked: { type: Boolean, reflect: true },

			/**
			 * Places the label first and then the switch after.
			 */
			labelFirst: { type: Boolean, attribute: "label-first", reflect: true }
		};
	}

	set checked(value) {
		const oldValue = this.__checked;
		this.__checked = value;
		this.requestUpdate("checked", oldValue);
		this.value = this.__checked;
	}

	get checked() {
		return this.__checked;
	}

	constructor() {
		super();
		this._clickFn = () => this.toggle();
		this._keyUpFn = e => { if(e.key === " ") this.toggle(); };

		this.size = "large";
		this.checked = false;
		this.labelFirst = false;
	}

	connectedCallback() {
		super.connectedCallback();
		this.addEventListener("click", this._clickFn);
		this.addEventListener("keyup", this._keyUpFn);
	}

	disconnectedCallback() {
		this.removeEventListener("keyup", this._keyUpFn);
		this.removeEventListener("click", this._clickFn);
		super.disconnectedCallback();
	}

	firstUpdated(_changedProperties) {
		super.firstUpdated(_changedProperties);
		this.setAttribute("tabindex", 0);
	}

	reset() {
		super.reset();
		this.checked = this.defaultValue;
	}

	toggle() {
		this.checked = !this.checked;
	}

	static get styles() {
		return css`
			:host {
				display: inline-block;
				cursor: pointer;
				outline: none;
			}
			
			.wrapper {
				display: flex;
				align-items: center;
		
				transition: opacity .2s;
			}
			
			.track {
				position: relative;
				background: var(--divider-color);
				border-radius: 3px;
				width: 44px;
				height: 22px;
				margin-right: 8px;
				padding: 1px;
	
				transition: background-color .2s;
			}
			
			.button {
				position: relative;
				background: #FFF;
				width: 22px;
				height: 100%;
				border-radius: 2px;

				transition: transform .2s, background .2s;
			}
			
			/* Not Checked State */
			:host(:not([checked])) .track > .button > .checkmark { display: none; }
			
			/* Checked State */
			:host([checked]) .track { background: var(--primary-color); }
			:host([checked]) .button { background: #FFF; transform: translateX(100%); }
		
			/* Disabled & Checked State */
			:host([disabled][checked]) .track { background: var(--divider-color); }
			
			/* Disabled State */
			:host([disabled]) .wrapper { opacity: 0.5; }
			:host([disabled]) .button { background: var(--secondary-text-color); }
			
			/* Focus State */
			:host([focused]) .track:after {
				content: '';
				display: block;
				position: absolute;
				top: -2px;
				bottom: -2px;
				left: -2px;
				right: -2px;
				border: 1px dashed var(--primary-color);
				border-radius: 3px;
				pointer-events: none;
			}
		
			/* Label First State */
			:host([label-first]) .wrapper { flex-direction: row-reverse; }
			:host([label-first]) .track { margin-right: 0; margin-left: 8px; }
		`;
	}

	render() {
		return html`
			<section class="wrapper">
				<div class="track">
					<div class="button">
						<aui-checkmark .size=${this.size} ?checked=${this.checked} .isDisabled=${this.isDisabled}></aui-checkmark>
					</div>
				</div>
	
				<div class="label">
					<slot></slot>
				</div>
			</section>
		`;
	}

}

export default AuiToggle;

customElements.define("aui-toggle", AuiToggle);